/** Returns today's date as YYYY-MM-DD in the user's local timezone (not UTC). */
export function getLocalTodayStr(): string {
  return new Date().toLocaleDateString("sv-SE");
}

export function parseLocalDate(dateInput: string | Date | null | undefined): Date {
  if (!dateInput) return new Date(NaN);
  if (dateInput instanceof Date) {
    return new Date(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate());
  }
  const str = String(dateInput);
  const datePart = str.includes("T") ? str.split("T")[0] : str.split(" ")[0];
  const [y, m, d] = datePart.split("-").map(Number);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return new Date(NaN);
  return new Date(y, m - 1, d);
}

/** Add days to a date string, returning YYYY-MM-DD. Uses local timezone. */
export function addDaysToDateStr(dateStr: string, days: number): string {
  const d = parseLocalDate(dateStr);
  if (isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function toDateInputValue(dateInput: string | Date | null | undefined): string {
  if (!dateInput) return "";
  if (dateInput instanceof Date) {
    const y = dateInput.getFullYear();
    const m = String(dateInput.getMonth() + 1).padStart(2, "0");
    const d = String(dateInput.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const str = String(dateInput);
  const datePart = str.includes("T") ? str.split("T")[0] : str.split(" ")[0];
  return datePart;
}

export function formatDateSafe(dateInput: string | Date | null | undefined): string {
  if (!dateInput) return "TBD";
  const d = parseLocalDate(dateInput);
  if (isNaN(d.getTime())) return "TBD";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function getTripDurationDays(
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined
): number | null {
  if (!startDate || !endDate) return null;
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return null;
  return diffDays === 0 ? 1 : diffDays;
}

export function getItineraryDayCount(trip: { itinerary?: any }): number | null {
  if (Array.isArray(trip.itinerary) && trip.itinerary.length > 0) {
    const dayBlocks = trip.itinerary.filter((b: any) => b.type !== "transport");
    return dayBlocks.length > 0 ? dayBlocks.length : trip.itinerary.length;
  }
  return null;
}

export function getEffectiveTripDays(trip: { itinerary?: any; startDate?: any; endDate?: any }): number | null {
  return getItineraryDayCount(trip) || getTripDurationDays(trip.startDate, trip.endDate);
}

/**
 * Sorts trips by status priority (open > idea > closed) then by createdAt descending
 * (most recently created first). Falls back to startDate if createdAt is unavailable.
 * Returns a new sorted array (does not mutate the input).
 */
export function sortTripsByStatusAndDate<T extends { status?: string | null; startDate?: string | Date | null; createdAt?: string | Date | null }>(
  trips: T[],
): T[] {
  const statusOrder: Record<string, number> = { open: 0, idea: 1, accepted: 2, finalized: 3, closed: 4 };
  return [...trips].sort((a, b) => {
    const aOrder = statusOrder[a.status || ""] ?? 99;
    const bOrder = statusOrder[b.status || ""] ?? 99;
    if (aOrder !== bOrder) return aOrder - bOrder;
    // Sort by createdAt descending (newest first), fallback to startDate
    const aTime = a.createdAt ? new Date(String(a.createdAt)).getTime() : (a.startDate ? new Date(String(a.startDate)).getTime() : 0);
    const bTime = b.createdAt ? new Date(String(b.createdAt)).getTime() : (b.startDate ? new Date(String(b.startDate)).getTime() : 0);
    return bTime - aTime;
  });
}

/** Format an array of ISO date strings into a human-readable string.
 *  - 1 day                   → "May 1, 2026"
 *  - Continuous range        → "May 1 - 3, 2026" / "Apr 29 - May 3, 2026"
 *  - Discrete days (gaps)    → "May 1, 3, 5, 2026" / "Apr 30, May 2, 2026"
 *  Dedupes and sorts input. Appends " (est.)" when isRolledForward. */
export function formatFestivalDates(dates: string[], isRolledForward?: boolean): string {
  if (dates.length === 0) return "TBA";

  // Dedupe + sort + parse — bail to TBA if any entry is unparseable
  const unique = Array.from(new Set(dates)).sort();
  const parsed = unique.map((d) => new Date(d + "T12:00:00"));
  if (parsed.some((d) => isNaN(d.getTime()))) return "TBA";

  const monthFmt = { month: "short" as const, day: "numeric" as const };
  const suffix = isRolledForward ? " (est.)" : "";
  const first = parsed[0];
  const last = parsed[parsed.length - 1];
  const year = first.getFullYear();

  if (parsed.length === 1) {
    return first.toLocaleDateString("en-US", { ...monthFmt, year: "numeric" }) + suffix;
  }

  // Detect continuous: every adjacent pair is exactly 1 day apart
  const DAY_MS = 24 * 60 * 60 * 1000;
  const isContinuous = parsed.every((d, i) => {
    if (i === 0) return true;
    return Math.round((d.getTime() - parsed[i - 1].getTime()) / DAY_MS) === 1;
  });

  if (isContinuous) {
    if (first.getMonth() === last.getMonth()) {
      return `${first.toLocaleDateString("en-US", monthFmt)} - ${last.getDate()}, ${year}` + suffix;
    }
    return `${first.toLocaleDateString("en-US", monthFmt)} - ${last.toLocaleDateString("en-US", { ...monthFmt, year: "numeric" })}`.replace(`, ${year}`, `, ${year}`) + suffix;
  }

  // Discrete days — comma-separated. Repeat month name only when it changes.
  const parts: string[] = [];
  let lastMonth = -1;
  for (const d of parsed) {
    if (d.getMonth() !== lastMonth) {
      parts.push(d.toLocaleDateString("en-US", monthFmt));
      lastMonth = d.getMonth();
    } else {
      parts.push(String(d.getDate()));
    }
  }
  return `${parts.join(", ")}, ${year}` + suffix;
}
